import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, MenuItem, Paper, TextField } from "@mui/material";
import { useFormik } from "formik";
import * as yup from "yup";
import { useSelector } from "react-redux";
import * as Sentry from "@sentry/react";
import { FieldWrapper, SectionHeader, StepActions, PageHeader } from "@components/ui_v2";
import { RootState, useAppDispatch } from "@store/store";
import { setAddressData } from "@store/slices/registerSlice";
import { setModalErrorVisible } from "@store/slices/appSlice";
import { CODIGO_POSTAL } from "@utils/expreciones";
import { useLazyGetByCpQuery } from "@api/postalCodes";
import { usePatchAddressMutation, usePostAddressMutation } from "@api/address";
import { useGetAddressTypeQuery } from "@api/catalogs";
import { usePostValidateNextStageMutation } from "@api/applications";
import { Address } from "@interfaces/catalogs";
import { ApiError } from "@interfaces/response";
import { ADDRESS_CODES, PATHS_TOFU_V2, STATUS_CATALOG } from "@helpers/constantes";
import useStatusConfig from "@hooks/useStatusConfig";

interface Colonia {
  id: number;
  name: string;
}

/** Placeholder de los campos que se llenan solos con el código postal. */
const FROM_CP = "Se llenará con el CP";

/**
 * Domicilio fiscal de la PFAE (`S_DomicilioEmpresa` del diseño, status CSDOE). Se pide cuando el usuario
 * elige declarar ingresos sin CIEC: sin el SAT no se puede leer de la constancia y Buró lo necesita.
 * Lógica del legacy `tofu/pm/DomicilioPfae`: el domicilio se liga a la persona PFAE (`legal_representative`)
 * y a la cuenta; al crearlo avanza el status y al volver a la pantalla se actualiza.
 */
export const DomicilioFiscalPfae = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { nextStatus, previousStatus } = useStatusConfig(STATUS_CATALOG.domicilio_pfae.code);

  const clientDetails = useSelector((state: RootState) => state.register.personal_data);
  const addressData = useSelector((state: RootState) => state.register.address_data);
  const legalRepresentative = useSelector((state: RootState) => state.register.legal_representative);
  const application = useSelector((state: RootState) => state.register.application);
  const accountUser = useSelector((state: RootState) => state.register.account_user);

  const [getCp] = useLazyGetByCpQuery();
  const { data: addressTypes } = useGetAddressTypeQuery();
  const [postAddress] = usePostAddressMutation();
  const [patchAddress] = usePatchAddressMutation();
  const [triggerPostNextStage] = usePostValidateNextStageMutation();

  const [listColonias, setListColonias] = useState<Colonia[]>([]);
  const [cpError, setCpError] = useState("");
  const [isFetchingCp, setIsFetchingCp] = useState(false);

  // A diferencia del legacy (particular, `CDPA`), aquí se guarda como domicilio fiscal (`CDFI`).
  const addressTypeId = addressTypes?.find((item: Address) => item.code === ADDRESS_CODES.fiscal.code)?.id ?? "";

  const formik = useFormik({
    initialValues: {
      cp: "",
      calle: "",
      num_ext: "",
      num_int: "",
      state_id: "",
      city_id: "",
      colony_id: "",
      state_name: "",
      city_name: "",
      locality_id: "",
      locality_name: "",
      otro: "",
    },
    validationSchema: yup.object().shape({
      cp: yup.string().required("Campo requerido").matches(CODIGO_POSTAL, "Código Postal inválido"),
      calle: yup.string().required("Campo requerido"),
      num_ext: yup.string().required("Campo requerido"),
      state_name: yup.string().required("Campo requerido"),
      city_name: yup.string(),
      locality_id: yup.string().required("Campo requerido"),
      locality_name: yup.string().required("Campo requerido"),
      colony_id: yup.string().required("Campo requerido"),
      otro: yup.string().when("colony_id", {
        is: "-1",
        then: (schema) => schema.required("Campo requerido"),
        otherwise: (schema) => schema.notRequired(),
      }),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      try {
        setSubmitting(true);

        if (!nextStatus) {
          throw new Error("No se encontró el siguiente status");
        }
        if (!addressTypeId) {
          throw new Error("No se encontró el tipo de dirección");
        }
        if (!legalRepresentative?.id) {
          throw new Error("No se encontró la persona de la solicitud");
        }

        const dataAddress = {
          street: values.calle,
          no_ext: values.num_ext,
          no_int: values.num_int,
          colonia_id: Number(values.colony_id) === -1 ? null : +values.colony_id,
          state_id: values.state_id ? +values.state_id : null,
          city_id: values.city_id ? +values.city_id : null,
          locality_id: values.locality_id ? +values.locality_id : null,
          country_id: 1,
          cp: values.cp,
          person_id: legalRepresentative.id,
          client_details_id: clientDetails.id,
          address_type_id: addressTypeId,
          other_neighborhood: values.otro,
        };

        let resultAddress = null;
        if (addressData && addressData.id) {
          resultAddress = await patchAddress({ addressId: addressData.id, data: dataAddress }).unwrap();
        } else {
          resultAddress = await postAddress(dataAddress).unwrap();

          // Igual que el legacy: el status avanza al crear el domicilio, no al editarlo.
          await triggerPostNextStage({
            applicationId: application.id,
            statusCode: nextStatus.code,
            username: `TOFU - ${accountUser.email}`,
          }).unwrap();
        }

        dispatch(
          setAddressData({
            id: resultAddress.data.id,
            street: values.calle,
            no_ext: values.num_ext,
            no_int: values.num_int,
            colony_id: +values.colony_id,
            state_id: +values.state_id,
            city_id: +values.city_id,
            locality_id: +values.locality_id,
            postal_code: values.cp,
            state_name: values.state_name,
            city_name: values.city_name,
            locality_name: values.locality_name,
            other_neighborhood: values.otro,
          }),
        );

        navigate(nextStatus.url);
      } catch (error) {
        Sentry.captureException(error, { extra: { component: "DomicilioFiscalPfae", values: formik.values } });
        dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
      } finally {
        setSubmitting(false);
      }
    },
  });

  const handleCp = async (value: string) => {
    try {
      setIsFetchingCp(true);
      const cpData = await getCp(value);
      if (cpData) {
        const selectedState = cpData.data?.states[0];
        const selectedCity = cpData.data?.cities[0];
        const selectedLocality = cpData.data?.localities[0];
        const selectedColonia = cpData.data?.colonias[0];

        if (!selectedState || !selectedColonia || !selectedLocality) {
          formik.setFieldValue("state_id", "");
          formik.setFieldValue("state_name", "");
          formik.setFieldValue("city_id", "");
          formik.setFieldValue("city_name", "");
          formik.setFieldValue("locality_id", "");
          formik.setFieldValue("locality_name", "");
          formik.setFieldValue("colony_id", "");
          setCpError("No existe el código postal");
          setListColonias([]);
          return;
        }

        setCpError("");
        formik.setFieldValue("state_id", selectedState.id);
        formik.setFieldValue("state_name", selectedState.name);
        formik.setFieldValue("city_id", selectedCity ? selectedCity.id : "");
        formik.setFieldValue("city_name", selectedCity ? selectedCity.name : "");
        formik.setFieldValue("locality_id", selectedLocality.id);
        formik.setFieldValue("locality_name", selectedLocality.name);
        formik.setFieldValue("colony_id", selectedColonia.id);
        setListColonias(cpData.data?.colonias);
      }
    } catch (error) {
      Sentry.captureException(error, { extra: { component: "DomicilioFiscalPfae-cp" } });
    } finally {
      setIsFetchingCp(false);
    }
  };

  // Al volver a la pantalla se precarga el domicilio guardado (mismo patrón que el legacy).
  useEffect(() => {
    if (addressData && addressData.id) {
      handleCp(addressData.postal_code);
      formik.setFieldValue("cp", addressData.postal_code);
      formik.setFieldValue("calle", addressData.street);
      formik.setFieldValue("num_ext", addressData.no_ext);
      formik.setFieldValue("num_int", addressData.no_int);
      setTimeout(() => {
        formik.setFieldValue("colony_id", addressData.colony_id);
        formik.setFieldValue("otro", addressData.other_neighborhood);
      }, 800);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addressData]);

  const hasCpData = listColonias.length > 0;

  return (
    <Box>
      <PageHeader
        eyebrow="Tu negocio · Domicilio"
        title="Domicilio fiscal de tu negocio"
        subtitle="Sin conexión al SAT no podemos leerlo de tu constancia, y Buró de Crédito lo necesita para localizar el expediente. Captúralo tal como aparece en tu constancia de situación fiscal."
      />

      <Paper variant="lendia_paper" sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <SectionHeader label="Dirección fiscal" first />
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
          <FieldWrapper
            label="Código Postal"
            loading={isFetchingCp}
            error={cpError || (formik.touched.cp && formik.errors.cp) || ""}
          >
            <TextField
              autoComplete="off"
              size="lendia_input"
              fullWidth
              name="cp"
              value={formik.values.cp}
              onBlur={formik.handleBlur}
              onChange={(e) => {
                const cp = e.target.value.replace(/[^0-9]/g, "");
                formik.setFieldValue("cp", cp);
                if (cp.length === 5) {
                  handleCp(cp);
                }
              }}
              placeholder="00000"
              inputProps={{
                maxLength: 5,
                style: { fontVariantNumeric: "tabular-nums", letterSpacing: "0.05em" },
                "data-testid": "domicilio-fiscal-cp-input",
              }}
              disabled={formik.isSubmitting}
            />
          </FieldWrapper>

          <FieldWrapper label="Calle o avenida" full error={(formik.touched.calle && formik.errors.calle) || ""}>
            <TextField
              autoComplete="off"
              size="lendia_input"
              fullWidth
              name="calle"
              value={formik.values.calle}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder="Calle o avenida"
              inputProps={{ maxLength: 100, "data-testid": "domicilio-fiscal-calle-input" }}
              disabled={formik.isSubmitting}
            />
          </FieldWrapper>

          <FieldWrapper label="No. Exterior" error={(formik.touched.num_ext && formik.errors.num_ext) || ""}>
            <TextField
              autoComplete="off"
              size="lendia_input"
              fullWidth
              name="num_ext"
              value={formik.values.num_ext}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder="Ej. 42"
              inputProps={{ maxLength: 50, "data-testid": "domicilio-fiscal-num-ext-input" }}
              disabled={formik.isSubmitting}
            />
          </FieldWrapper>
          <FieldWrapper label="No. Interior" optionalSuffix="(opcional)">
            <TextField
              autoComplete="off"
              size="lendia_input"
              fullWidth
              name="num_int"
              value={formik.values.num_int}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder="Ej. Piso 4, Of. 401"
              inputProps={{ maxLength: 50, "data-testid": "domicilio-fiscal-num-int-input" }}
              disabled={formik.isSubmitting}
            />
          </FieldWrapper>

          <FieldWrapper label="Colonia" full error={(formik.touched.colony_id && formik.errors.colony_id) || ""}>
            {hasCpData ? (
              <TextField
                autoComplete="off"
                size="lendia_input"
                select
                fullWidth
                name="colony_id"
                value={formik.values.colony_id}
                onBlur={formik.handleBlur}
                onChange={(e) => {
                  formik.handleChange(e);
                  if (+e.target.value === -1) {
                    formik.setFieldValue("otro", "");
                  }
                }}
                inputProps={{ "data-testid": "domicilio-fiscal-colonia-select" }}
                disabled={formik.isSubmitting}
              >
                {listColonias.map((colonia) => (
                  <MenuItem key={colonia.id} value={colonia.id}>
                    {colonia.name}
                  </MenuItem>
                ))}
              </TextField>
            ) : (
              // Como en el diseño: antes del CP la colonia es un campo vacío de solo lectura.
              <TextField size="lendia_input" fullWidth value="" placeholder={FROM_CP} disabled />
            )}
          </FieldWrapper>

          {String(formik.values.colony_id) === "-1" && (
            <FieldWrapper label="Otra colonia" full error={(formik.touched.otro && formik.errors.otro) || ""}>
              <TextField
                autoComplete="off"
                size="lendia_input"
                fullWidth
                name="otro"
                value={formik.values.otro}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                inputProps={{ maxLength: 100, "data-testid": "domicilio-fiscal-otra-colonia-input" }}
                disabled={formik.isSubmitting}
              />
            </FieldWrapper>
          )}

          <FieldWrapper label="Ciudad">
            <TextField
              size="lendia_input"
              fullWidth
              name="city_name"
              value={formik.values.city_name}
              placeholder={FROM_CP}
              disabled
              inputProps={{ "data-testid": "domicilio-fiscal-ciudad-input" }}
            />
          </FieldWrapper>
          <FieldWrapper label="Municipio / Delegación">
            <TextField
              size="lendia_input"
              fullWidth
              name="locality_name"
              value={formik.values.locality_name}
              placeholder={FROM_CP}
              disabled
              inputProps={{ "data-testid": "domicilio-fiscal-municipio-input" }}
            />
          </FieldWrapper>
          <FieldWrapper label="Entidad federativa" full>
            <TextField
              size="lendia_input"
              fullWidth
              name="state_name"
              value={formik.values.state_name}
              placeholder={FROM_CP}
              disabled
              inputProps={{ "data-testid": "domicilio-fiscal-estado-input" }}
            />
          </FieldWrapper>
        </Box>
      </Paper>

      <StepActions
        // Regreso según `status_config`; si no lo trae, a la decisión de ingresos.
        onBack={() => navigate(previousStatus?.url || PATHS_TOFU_V2.decisionIngreso.path)}
        onNext={formik.submitForm}
        nextDisabled={!formik.isValid || formik.isSubmitting || isFetchingCp}
        backTestId="domicilio-fiscal-back-button"
        nextTestId="domicilio-fiscal-continue-button"
      />
    </Box>
  );
};
